import { use, useEffect, useSyncExternalStore } from 'react';

import { SiteContext } from 'src/context/site-context';
import { BuildType } from 'src/enums/build/build-type';
import { FEATURE_FLAG, getFeatureFlagAtClub } from 'src/helpers/feature-flag-helper';
import { getLocalStorageItem, setLocalStorageItem } from 'src/helpers/local-storage-helper';
import { NewLogoStorageKey } from 'src/helpers/new-logo-storage-keys';

const flagsBySiteId = new Map<string, boolean>();
const requestedSiteIds = new Set<string>();
const listeners = new Set<() => void>();

const subscribe = (listener: () => void) => {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
};

const readPersistedFlags = (): Partial<Record<string, unknown>> => {
  try {
    const parsed: unknown = JSON.parse(
      getLocalStorageItem(NewLogoStorageKey.useNewLogoByClubId) ?? '{}'
    );
    return typeof parsed === 'object' && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

const getPersistedFlag = (siteId: string): boolean | undefined => {
  const persisted = readPersistedFlags()[siteId];
  return typeof persisted === 'boolean' ? persisted : undefined;
};

const getServerSnapshot = (): boolean => FEATURE_FLAG.USE_NEW_LOGO.defaultValue;

const getSnapshot = (siteId: string): boolean =>
  flagsBySiteId.get(siteId) ?? getPersistedFlag(siteId) ?? FEATURE_FLAG.USE_NEW_LOGO.defaultValue;

const requestFlag = (siteId: string) => {
  if (requestedSiteIds.has(siteId)) {
    return;
  }
  requestedSiteIds.add(siteId);

  void getFeatureFlagAtClub(siteId, FEATURE_FLAG.USE_NEW_LOGO)
    .then((isEnabled) => {
      flagsBySiteId.set(siteId, isEnabled);
      listeners.forEach((listener) => listener());
      setLocalStorageItem(
        NewLogoStorageKey.useNewLogoByClubId,
        JSON.stringify({ ...readPersistedFlags(), [siteId]: isEnabled })
      );
    })
    .catch(() => {
      requestedSiteIds.delete(siteId);
    });
};

const useShouldUseNewLogo = (): boolean => {
  const { buildType, isDL, isGlobalPage, siteId } = use(SiteContext);
  const shouldCheckClubFeatureFlag = isDL && !isGlobalPage;

  const isNewLogoEnabledAtClub = useSyncExternalStore(
    subscribe,
    () => getSnapshot(siteId),
    getServerSnapshot
  );

  useEffect(() => {
    if (shouldCheckClubFeatureFlag) {
      requestFlag(siteId);
    }
  }, [shouldCheckClubFeatureFlag, siteId]);

  if (!isDL) {
    return false;
  }

  const isNewLogoEnabledAtGlobal =
    buildType === BuildType.DAVID_LLOYD_ITALY ||
    buildType === BuildType.DAVID_LLOYD_BELGIUM ||
    buildType === BuildType.DAVID_LLOYD_GERMANY;

  return isGlobalPage ? isNewLogoEnabledAtGlobal : isNewLogoEnabledAtClub;
};

export default useShouldUseNewLogo;
